/**
 * Module-level "session expired" flag exposed as a React hook via
 * `useSyncExternalStore`.
 *
 * The flag flips to `true` when `api-client` emits a `session-expired`
 * event (token refresh failed). Polling hooks like `usePipelineStatus`
 * read it via `useIsSessionExpired()` and pause their queries so we
 * don't spam the backend with requests that will all 401 anyway.
 *
 * Cleared when Supabase's auth-state listener observes a fresh
 * `SIGNED_IN` event (the user successfully re-authenticated).
 */

import { useSyncExternalStore } from 'react';

let expired = false;
const listeners = new Set<() => void>();

function notifyListeners() {
  listeners.forEach(fn => fn());
}

export const sessionState = {
  get isExpired(): boolean {
    return expired;
  },
  setExpired(value: boolean): void {
    if (expired === value) return;
    expired = value;
    notifyListeners();
  },
};

function subscribe(callback: () => void): () => void {
  listeners.add(callback);
  return () => {
    listeners.delete(callback);
  };
}

/**
 * Subscribe a React component to the "session expired" flag. Returns
 * `true` while the user must re-login, `false` otherwise. Safe to call
 * from any component - the underlying store is process-global.
 */
export function useIsSessionExpired(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => expired,
    () => false // server snapshot - SSR-safe, even though we don't SSR
  );
}
